import { Fragment, ReactNode, useCallback, useMemo } from "react"
import {
  Block,
  Button,
  ButtonProps,
  ChevronDown,
  StatefulPopover,
  useStyletron,
} from "@mezo-org/mezo-clay"

import { DepositWithWithdrawalStatus } from "#/redux-state"
import { useDepositBox } from "#/shared/hooks"
import { BridgeableTokens } from "#/shared/types"

import OverviewBridgeDepositButton from "./OverviewBridgeDepositButton"

type OverviewUnbridgedAssetsItemActionsProps = {
  deposit: DepositWithWithdrawalStatus
  isDepositLocked: boolean
  isDepositBridgeable: boolean
}

function OverviewUnbridgedAssetsItemActionDropdown({
  elements,
}: {
  elements: ReactNode[]
}) {
  const [css, theme] = useStyletron()

  if (!elements.length) return null

  if (elements.length === 1) return elements[0]

  return (
    <StatefulPopover
      autoFocus={false}
      placement="bottom"
      content={
        <Block className={css({ display: "flex", flexDirection: "column" })}>
          {elements.map((el, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Fragment key={index}>{el}</Fragment>
          ))}
        </Block>
      }
      overrides={{
        Body: { style: () => ({ minWidth: "100px" }) },
        Inner: {
          style: () => ({ background: theme.colors.buttonSecondaryFill }),
        },
      }}
    >
      <Button
        $as="div"
        shape="pill"
        kind="secondary"
        size="small"
        endEnhancer={() => <ChevronDown size={theme.sizing.scale600} />}
        overrides={{ BaseButton: { style: { width: "100px" } } }}
      >
        Actions
      </Button>
    </StatefulPopover>
  )
}

export default function OverviewUnbridgedAssetsItemActions({
  deposit,
  isDepositLocked,
  isDepositBridgeable,
}: OverviewUnbridgedAssetsItemActionsProps) {
  const { isAutoMigrationEnabled, token, mintedAmount, depositId, amount } =
    deposit

  const { openWithdrawalModal, openReturnStbtcModal } = useDepositBox(deposit)

  const hasStbtcToReturn = useMemo(
    () => BigInt(mintedAmount) > 0n,
    [mintedAmount],
  )

  const getWithdrawButton = useCallback(
    ({ shape }: Pick<ButtonProps, "shape">) => (
      <Button
        shape={shape}
        kind="secondary"
        size="small"
        onClick={openWithdrawalModal}
      >
        Withdraw
      </Button>
    ),
    [openWithdrawalModal],
  )

  if (hasStbtcToReturn)
    return (
      <Button
        shape="pill"
        kind="secondary"
        size="small"
        onClick={openReturnStbtcModal}
      >
        Return stBTC
      </Button>
    )

  if (isAutoMigrationEnabled && (isDepositBridgeable || token === "WBTC")) {
    return (
      <Button disabled kind="secondary" shape="pill" size="small">
        In progress
      </Button>
    )
  }

  if (!isDepositBridgeable) {
    return isDepositLocked ? (
      <Button disabled kind="secondary" shape="pill" size="small">
        Locked
      </Button>
    ) : (
      getWithdrawButton({ shape: "pill" })
    )
  }

  return (
    <OverviewUnbridgedAssetsItemActionDropdown
      elements={[
        <OverviewBridgeDepositButton
          shape={isDepositLocked ? "pill" : "default"}
          token={token as BridgeableTokens}
          depositAmount={BigInt(amount)}
          depositId={depositId}
        />,
        ...(isDepositLocked ? [] : [getWithdrawButton({ shape: "default" })]),
      ]}
    />
  )
}
